import { Heading, Tag, Text } from "@ttc3k/trekker";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { Shimmer } from "shared/components/atoms";
import { CopyToClipboard } from "shared/components/molecules";
import {
  DATE_PATTERN_STANDARD,
  formatPhoneNumber,
  getMultilingualString,
} from "shared/utils";
import { Flex, styled } from "styled-system/jsx";

type SurveyResponseHeaderProps = {
  surveyResponse: any;
  /** When set with a linked `person._id`, the submitter name opens the people profile. */
  onOpenPersonProfile?: (personId: string) => void;
};

export const SurveyResponseHeader = ({
  surveyResponse,
  onOpenPersonProfile,
}: SurveyResponseHeaderProps) => {
  const { t } = useTranslation("people", {
    keyPrefix: "FORM_RESPONSE_MODAL",
  });

  const surveyName = surveyResponse?.survey?.name
    ? getMultilingualString(surveyResponse.survey.name)
    : null;
  const title = surveyName || t("TITLE");

  const status: string | undefined = surveyResponse?.status;

  const person = surveyResponse?.person;
  const personFullName = person
    ? `${(person.firstName ?? "").trim()} ${(person.lastName ?? "").trim()}`.trim()
    : surveyResponse?.history?.[0]?.personName ?? null;

  const personId =
    person?._id != null ? String(person._id) : null;
  const canOpenProfile =
    Boolean(personFullName && personId && onOpenPersonProfile);

  const date = surveyResponse?.createdDateISO;

  return (
    <Flex
      flexDir="column"
      gap="300"
      p="400"
      bgColor="white"
      borderRadius="150"
      boxShadow="0 10px 15px -5px rgba(10, 10, 10, 0.10), 0 5px 5px -2px rgba(10, 10, 10, 0.05)"
    >
      {surveyResponse ? (
        <>
          <Flex
            alignItems="flex-start"
            gap="200"
            width="full"
            flexWrap={{ base: "wrap", sm: "nowrap" }}
          >
            <Heading size="h4" flex="1" minW="0" pr={{ sm: "200" }}>
              {title}
            </Heading>
            <Flex
              alignItems="center"
              gap="150"
              flexShrink={0}
              flexWrap="wrap"
              justifyContent="flex-end"
              w={{ base: "full", sm: "auto" }}
            >
              {status === "PENDING_REVIEW" && (
                <Tag visual="warning" mode="light">
                  <styled.span whiteSpace="nowrap">{t("PENDING_REVIEW")}</styled.span>
                </Tag>
              )}
              {status === "SUBMITTED" && (
                <Tag visual="success" mode="light">
                  <styled.span whiteSpace="nowrap">{t("SUBMITTED")}</styled.span>
                </Tag>
              )}
              {status === "DRAFT" && (
                <Tag visual="primary" mode="light">
                  <styled.span whiteSpace="nowrap">{t("DRAFT")}</styled.span>
                </Tag>
              )}
              {status === "REJECTED" && (
                <Tag visual="destructive" mode="light">
                  <styled.span whiteSpace="nowrap">{t("REJECTED")}</styled.span>
                </Tag>
              )}
            </Flex>
          </Flex>
          <Flex flexDir="column" gap="0">
            {personFullName &&
              (canOpenProfile ? (
                <Flex
                  alignItems="baseline"
                  gap="100"
                  flexWrap="wrap"
                  maxW="full"
                >
                  <Text
                    visual="bodyMedium"
                    color="text.mid"
                    lineClamp={1}
                    flexShrink={0}
                  >
                    {t("SENT_FROM_PREFIX")}
                  </Text>
                  <styled.button
                    type="button"
                    font="inherit"
                    fontSize="inherit"
                    lineHeight="inherit"
                    fontWeight="500"
                    bg="transparent"
                    border="none"
                    p="0"
                    m="0"
                    cursor="pointer"
                    textAlign="left"
                    color="text.accent.blue.mid"
                    textDecoration="underline"
                    lineClamp={1}
                    wordBreak="break-all"
                    minW="0"
                    onClick={() =>
                      personId && onOpenPersonProfile?.(personId)
                    }
                  >
                    {personFullName}
                  </styled.button>
                </Flex>
              ) : (
                <Text
                  visual="bodyMedium"
                  color="text.mid"
                  lineClamp={1}
                  wordBreak="break-all"
                >
                  {t("SENT_FROM", { sentFrom: personFullName })}
                </Text>
              ))}
            {person?.email && (
              <CopyToClipboard
                text={person.email}
                visual="bodyMedium"
                color="text.mid"
                opacity={1}
                buttonProps={{ width: "fit-content" }}
              />
            )}
            {person?.phone && (
              <CopyToClipboard
                text={formatPhoneNumber(person.phone)}
                visual="bodyMedium"
                color="text.mid"
                opacity={1}
                buttonProps={{ width: "fit-content" }}
              />
            )}
            {date && (
              <Text visual="bodyMedium" color="text.mid">
                {format(new Date(date), DATE_PATTERN_STANDARD)}
              </Text>
            )}
          </Flex>
        </>
      ) : (
        <>
          <Shimmer h="36px" w="full" />
          <Shimmer h="72px" w="full" />
        </>
      )}
    </Flex>
  );
};
